import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Barcode from 'react-barcode';
import authAxios from '../../utils/authAxios';
import SecureImage from '../../components/SecureImage';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import AssignedItemCard from '../../components/AssignedItemCard';
import { NO_PLU_LABEL, NO_PLU_DISPLAY, digitsOnly, normalizeUpcOptionPayload } from '../../utils/customerSpecUpc';
import { gradeColor } from '../../utils/gradeColor';
import { resolveForStyle, canonicalCustomerId } from '../../utils/bagLabelRules';
import { BAGLABEL_FOLDER, toFilename } from '../../components/BagLabelModal';
import { parseCsvList as parseCsvListShared } from '../../utils/csvList';
import { getOrderedSizesForCommodity } from '../../utils/sizeOptions';
import { commodityColor as colorForCommodity } from '../../utils/commodityColor';
import {
  PageWrapper, FiltersContainer, ContentWrapper, TopBar, ScrollableContent,
  NoResultsMessage, SkeletonCard, CardGrid, CardWrapper, ImageWrapper,
  InfoSection, Label, StyledInput, Pill, UniversalHeader, ClearButton,
  IndexPanelBackdrop, IndexPanelContainer, IndexPanelContent, FirstSection,
  LogoBox, Row, GreyBadge, GreenTitle, FooterDate, FooterEditor,
  ColumnGrid, ColumnCard, SpecNameGroup, UniversalHeader as UniversalStrip,
  GroupCard, GroupHeaderBar, TableGrid, TableRow, CellKey, CellValue, DividerRow, NotesArea,
  PanelCloseButton, LightboxBackdrop, LightboxImage
  , ValuePillWrap, ValuePill
  , AssignedTier, AssignedTierLabel, AssignedItemsWrap
} from '../../styles/CustomerSpecsIndexStyles';

const CDN_BASE = 'https://thevaultmedia.azureedge.net/vault-images';
const ALL_DEST_TOKEN = '__ALL__';
const DEST_ALL_FILTER = '__ALL_FILTER__';
// Commodity palette + hash fallback: utils/commodityColor.js, imported as
// colorForCommodity — the same source the Bag Traceability and Package Index
// pills use, so a commodity reads identically everywhere.

// (BWC formatting not used on index cards)

function gs1CheckDigit(baseDigits) {
  const digits = String(baseDigits || '');
  let sum = 0;
  for (let i = digits.length - 1, pos = 1; i >= 0; i--, pos++) {
    const n = Number(digits[i]);
    if (!Number.isInteger(n)) return null;
    sum += n * (pos % 2 === 1 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

function hasValidGs1CheckDigit(digits) {
  const d = String(digits || '');
  if (!/^\d+$/.test(d) || d.length < 2) return false;
  return gs1CheckDigit(d.slice(0, -1)) === Number(d[d.length - 1]);
}

function barcodeConfigForUpc(value) {
  const digits = digitsOnly(value);
  if (!digits) return null;
  if (digits.length === 12 && hasValidGs1CheckDigit(digits)) return { value: digits, format: 'UPC' };
  if (digits.length === 13 && hasValidGs1CheckDigit(digits)) return { value: digits, format: 'EAN13' };
  if (digits.length === 8 && hasValidGs1CheckDigit(digits)) return { value: digits, format: 'EAN8' };
  return { value: digits, format: 'CODE128' };
}

function buildPidBagsImageUrl(raw) {
  if (!raw) return '';
  let val = String(raw).trim();
  if (!val) return '';

  // Some legacy rows store an encoded URL/path; try decoding once.
  if (/%[0-9A-Fa-f]{2}/.test(val)) {
    try { val = decodeURIComponent(val); } catch {}
  }

  // Absolute URL? Use it directly.
  try {
    // eslint-disable-next-line no-new
    new URL(val);
    return val;
  } catch {
    // not absolute
  }

  // Strip any prefix up to /vault-images/, then remove PIDBags folder if present.
  val = val.replace(/^.*\/vault-images\//i, '');
  val = val.replace(/^\/+/, '').replace(/^PIDBags\//i, '');
  val = val.split('?')[0].split('#')[0];

  const parts = val.split('/').filter(Boolean);
  const filename = parts.length ? parts[parts.length - 1] : '';
  if (!filename) return '';

  return `${CDN_BASE}/PIDBags/${encodeURIComponent(filename)}`;
}

function slugifyCompany(name) {
  const raw = String(name || '').trim();
  if (!raw) return 'upload';
  const dashified = raw.replace(/\s+/g, '-');
  const safe = dashified.replace(/[^a-zA-Z0-9._-]/g, '');
  return safe.replace(/[\\/]+/g, '').replace(/^\.+/, '').slice(0, 120) || 'upload';
}

const CustomerSpecsIndexPage = () => {
  const [loading, setLoading] = useState(true);
  const [allSpecs, setAllSpecs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [stylesAll, setStylesAll] = useState([]);
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [destination, setDestination] = useState('');
  const [domesticImport, setDomesticImport] = useState('');
  const [commodityId, setCommodityId] = useState('');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelSpec, setPanelSpec] = useState(null);
  const [upcMap, setUpcMap] = useState(null); // kept for UPC row(s) in other columns later if needed
  const [upcRefByValue, setUpcRefByValue] = useState(null); // pidupc:id / pidlabel:id -> normalized UPC ref
  const [pluByLabelId, setPluByLabelId] = useState(null); // LabelID -> [plu records]
  const [pluImagesCache, setPluImagesCache] = useState({}); // PLU id -> string[]
  const [panelPluImages, setPanelPluImages] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxGallery, setLightboxGallery] = useState(null); // string[]
  const [methodsById, setMethodsById] = useState(null); // id -> name
  const [methodImagesCache, setMethodImagesCache] = useState({}); // method id -> string[]
  const [stylesById, setStylesById] = useState(null); // id -> style name
  const [tihiRows, setTihiRows] = useState(null); // array of { UniqueID, TiHi, ImageLink }
  const [tihiById, setTihiById] = useState(null); // id -> { TiHi, ImageLink }
  const [caseTagsByName, setCaseTagsByName] = useState(null); // CaseTagName -> { ImageLink, ... }
  const [bagsByLabelId, setBagsByLabelId] = useState(null); // LabelID -> [{UniqueID,...}]
  const [bagLabelRows, setBagLabelRows] = useState(null); // dbo.BagLabels rows (Bag Label card)
  const [bagImagesCache, setBagImagesCache] = useState({}); // BagID -> string[]
  const [pidBagUpcById, setPidBagUpcById] = useState(null); // Bag UniqueID -> UPC string
  const [panelDestExpanded, setPanelDestExpanded] = useState(false);

  // Load minimal dropdowns + specs for index display
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [specsRes, custDD, commDD, sizeDD, packRes] = await Promise.allSettled([
          authAxios.get('/api/customer-specs'),
          authAxios.get('/api/customers/dropdown'),
          authAxios.get('/api/commodities/dropdown'),
          authAxios.get('/api/sizeoptions/dropdown'),
          authAxios.get('/api/packtypes'),
        ]);

        // Required for index to function
        if (specsRes.status !== 'fulfilled') throw specsRes.reason;
        if (custDD.status !== 'fulfilled') throw custDD.reason;

        // Non-critical reference data is best-effort: keep page usable if one call fails
        if (commDD.status !== 'fulfilled') console.warn('[CustomerSpecsIndex] commodities load degraded', commDD.reason);
        if (sizeDD.status !== 'fulfilled') console.warn('[CustomerSpecsIndex] size options load degraded', sizeDD.reason);
        if (packRes.status !== 'fulfilled') console.warn('[CustomerSpecsIndex] pack types load degraded', packRes.reason);

        setAllSpecs(specsRes.value?.data || []);
        setCustomers({ dropdown: custDD.value?.data || [] });
        setCommodities(commDD.status === 'fulfilled' ? (commDD.value?.data || []) : []);
        setSizes(sizeDD.status === 'fulfilled' ? (sizeDD.value?.data || []) : []);
        setStylesAll(packRes.status === 'fulfilled' ? (packRes.value?.data || []) : []);
      } catch (e) {
        console.error('[CustomerSpecsIndex] load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load normalized UPC refs only when panel opens first time.
  useEffect(() => {
    if (!panelOpen || upcRefByValue) return;
    (async () => {
      try {
        const res = await authAxios.get('/api/customer-specs/upc-options');
        const normalized = normalizeUpcOptionPayload(res.data || {});
        const refMap = {};
        const pidUpcMap = {};
        const pidLabelMap = {};
        (normalized.refs || []).forEach((ref) => {
          refMap[ref.value] = ref;
          if (ref.sourceType === 'PIDUPC') pidUpcMap[Number(ref.sourceId)] = ref.digits;
          if (ref.sourceType === 'PID_LABEL') pidLabelMap[Number(ref.sourceId)] = ref.digits;
        });
        setUpcRefByValue(refMap);
        setUpcMap(pidUpcMap);
        setPidBagUpcById(pidLabelMap);
      } catch (e) {
        console.error('[CustomerSpecsIndex] failed to load UPC refs', e);
      }
    })();
  }, [panelOpen, upcRefByValue]);

  // Load PIDPLU map when panel opens the first time
  useEffect(() => {
    if (!panelOpen || pluByLabelId) return;
    (async () => {
      try {
        const res = await authAxios.get('/api/pidplu');
        const map = {};
        (res.data || []).forEach(row => {
          const k = String(row.LabelID || '');
          if (!k) return;
          if (!map[k]) map[k] = [];
          map[k].push(row);
        });
        setPluByLabelId(map);
      } catch (e) {
        console.error('[CustomerSpecsIndex] failed to load PIDPLU map', e);
      }
    })();
  }, [panelOpen, pluByLabelId]);

  // Load methods, styles, tihi, case tags when panel opens
  useEffect(() => {
    if (!panelOpen) return;
    (async () => {
      try {
        if (!methodsById) {
          const res = await authAxios.get('/api/methods');
          const map = {};
          (res.data || []).forEach(m => { map[Number(m.UniqueID)] = m.MethodName || m.Method; });
          setMethodsById(map);
        }
        if (!stylesById) {
          const res = await authAxios.get('/api/packtypes');
          const map = {};
          (res.data || []).forEach(s => { map[Number(s.UniqueID)] = s.Style; });
          setStylesById(map);
        }
        if (!tihiRows || !tihiById) {
          const res = await authAxios.get('/api/tihi');
          const rows = res.data || [];
          const map = {};
          rows.forEach(r => { map[Number(r.UniqueID)] = { TiHi: r.TiHi, ImageLink: r.ImageLink }; });
          setTihiRows(rows);
          setTihiById(map);
        }
        if (!caseTagsByName) {
          const res = await authAxios.get('/api/casetags');
          const map = {};
          (res.data || []).forEach(ct => { map[String(ct.CaseTagName || '').trim()] = ct; });
          setCaseTagsByName(map);
        }
        if (!bagsByLabelId) {
          try {
            const res = await authAxios.get('/api/pidbags');
            const map = {};
            const upcMapBag = {};
            (res.data || []).forEach(row => {
              const lid = String(row.LABEL_ID || row.LabelID || '').trim();
              if (!lid) return;
              if (!map[lid]) map[lid] = [];
              map[lid].push(row);
              const bid = Number(row.UniqueID);
              if (Number.isFinite(bid)) {
                const u = String(row.UPC || '').trim();
                if (u) upcMapBag[bid] = u;
              }
            });
            setBagsByLabelId(map);
            setPidBagUpcById(upcMapBag);
          } catch (e) {
            console.error('[CustomerSpecsIndex] failed to load PID Bags', e);
          }
        }
        if (!bagLabelRows) {
          const res = await authAxios.get('/api/baglabels');
          setBagLabelRows(res.data || []);
        }
      } catch (e) {
        console.error('[CustomerSpecsIndex] failed to load column3 refs', e);
      }
    })();
  }, [panelOpen, methodsById, stylesById, tihiRows, tihiById, caseTagsByName, bagsByLabelId, bagLabelRows]);

  // Stable identity for effect/memo deps; the quote-aware logic lives in ../../utils/csvList (fix-it M4).
  const parseCsvList = useCallback((input) => parseCsvListShared(input), []);

  // Resolve PLU images for the current spec (support multiple labels / PLUs)
  useEffect(() => {
    (async () => {
      setPanelPluImages([]);
      if (!panelOpen || !panelSpec || !pluByLabelId) return;
      const labels = parseCsvList(panelSpec.LabelIDsCsv);
      const allIds = [];
      labels.forEach(lbl => (pluByLabelId[String(lbl)] || []).forEach(r => allIds.push(Number(r.UniqueID))));
      const uniqueIds = Array.from(new Set(allIds));

      const nextCache = { ...pluImagesCache };
      const images = [];
      for (const id of uniqueIds) {
        let arr = nextCache[id];
        if (!arr) {
          try {
            const res = await authAxios.get(`/api/pidplu/images?pluId=${id}`);
            arr = Array.isArray(res.data) ? res.data : [];
          } catch { arr = []; }
          nextCache[id] = arr;
        }
        arr.forEach(u => images.push(u));
      }
      setPluImagesCache(nextCache);
      setPanelPluImages(Array.from(new Set(images)));
    })();
  }, [panelOpen, panelSpec, pluByLabelId, parseCsvList, pluImagesCache]);



  // Customer dropdown: only customers that have at least one spec entered
  const customerOptions = useMemo(() => {
    const idsWithSpecs = new Set(
      (allSpecs || [])
        .map(s => Number(s.CustomerID))
        .filter(v => Number.isFinite(v) && v > 0)
    );
    return (customers.dropdown || [])
      .filter(c => idsWithSpecs.has(Number(c.UniqueID)))
      .map(c => ({ value: String(c.UniqueID), label: String(c.Customer || '') }));
  }, [allSpecs, customers.dropdown]);

  // Are required filters selected?
  const requiredSelected = useMemo(() => Boolean(customerId && domesticImport), [customerId, domesticImport]);

  const anyFiltersSelected = useMemo(() => {
    return Boolean(
      String(search || '').trim() ||
      customerId ||
      domesticImport ||
      destination ||
      commodityId ||
      (selectedSizes || []).length ||
      (selectedStyles || []).length
    );
  }, [search, customerId, domesticImport, destination, commodityId, selectedSizes, selectedStyles]);

  const clearAllFilters = useCallback(() => {
    setSearch('');
    setCustomerId('');
    setDomesticImport('');
    setDestination('');
    setCommodityId('');
    setSelectedSizes([]);
    setSelectedStyles([]);
  }, []);

  const commodityIdByName = useMemo(() => {
    const map = new Map();
    (commodities || []).forEach((c) => {
      const key = String(c.Commodity || '').trim().toLowerCase();
      if (!key) return;
      map.set(key, Number(c.UniqueID));
    });
    return map;
  }, [commodities]);

  const resolveCommodityIdForSpec = useCallback((spec) => {
    const raw = Number(spec?.CommodityID);
    if (Number.isFinite(raw) && raw > 0) return raw;
    const name = String(spec?.CommodityName || spec?.Commodity || '').trim().toLowerCase();
    const fromName = name ? commodityIdByName.get(name) : null;
    return Number.isFinite(fromName) ? fromName : null;
  }, [commodityIdByName]);

  // Every spec for this Customer + Import/Domestic (ignore optional filters) — the
  // pool that decides whether the pair has anything at all to show.
  const specsForRequiredOnly = useMemo(() => {
    if (!requiredSelected) return [];
    return (allSpecs || []).filter(s =>
      String(s.CustomerID) === String(customerId) &&
      String(s.DomesticOrImport || '') === domesticImport
    );
  }, [allSpecs, requiredSelected, customerId, domesticImport]);

  // Universal cards available for this Customer + Import/Domestic (ignore optional filters)
  const universalForRequiredOnly = useMemo(
    () => specsForRequiredOnly.filter(s => Boolean(s.Universal)),
    [specsForRequiredOnly]
  );

  // Size options for the selected commodity, scoped to the Domestic/Import filter and
  // ordered by the configured SortOrder (Import falls back to Domestic when none exist).
  const sizeOptionsForCommodity = useMemo(
    () => getOrderedSizesForCommodity(sizes, commodityId, domesticImport),
    [commodityId, domesticImport, sizes]
  );

  const availableSizeAddOptions = useMemo(() => {
    const all = sizeOptionsForCommodity;
    return all.filter(s => !(selectedSizes || []).includes(s));
  }, [sizeOptionsForCommodity, selectedSizes]);

  // When commodity changes, prune/reset size selections to valid ones
  useEffect(() => {
    if (!commodityId) {
      setSelectedSizes([]);
      return;
    }
    const allowed = new Set(sizeOptionsForCommodity);
    setSelectedSizes(prev => (prev || []).filter(s => allowed.has(s)));
  }, [commodityId, sizeOptionsForCommodity]);

  const styleNameById = useMemo(() => {
    const map = {};
    (stylesAll || []).forEach(s => { map[Number(s.UniqueID)] = s.Style; });
    return map;
  }, [stylesAll]);

  const availableStyleAddOptions = useMemo(() => {
    return (stylesAll || []).filter(st => !(selectedStyles || []).some(id => Number(id) === Number(st.UniqueID)));
  }, [stylesAll, selectedStyles]);

  // Base filter with only required filters applied (+ optional search/commodity)
  const baseRequiredFiltered = useMemo(() => {
    if (!requiredSelected) return [];
    const q = search.trim();
    const normalizeForSearch = (str) => String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
    const tokens = q.split(/\s+/).map(normalizeForSearch).filter(Boolean);
    return (allSpecs || []).filter(s => {
      if (String(s.CustomerID) !== String(customerId)) return false;
      if (String(s.DomesticOrImport || '') !== domesticImport) return false;
      if (commodityId) {
        const resolved = resolveCommodityIdForSpec(s);
        if (Number(resolved) !== Number(commodityId)) return false;
      }
      if ((selectedSizes || []).length > 0) {
        const sizesCsv = parseCsvList(s.SizesCsv);
        const sizesSet = new Set(sizesCsv);
        // Require ALL selected sizes to be present in the spec
        const matchesAll = (selectedSizes || []).every(sz => sizesSet.has(sz));
        if (!matchesAll) return false;
      }
      if ((selectedStyles || []).length > 0) {
        const styleIds = String(s.StyleIDsCsv || '').split(',').map(x => Number(String(x).trim())).filter(v => !Number.isNaN(v));
        const styleSet = new Set(styleIds);
        const matchesAllStyles = (selectedStyles || []).every(id => styleSet.has(Number(id)));
        if (!matchesAllStyles) return false;
      }
      if (tokens.length === 0) return true;
      const hayRaw = [
        s.CustomerName,
        s.SpecName,
        s.CommodityName || s.Commodity,
        s.CaseTagName,
        s.DestinationsCsv,
        s.VarietiesCsv,
        s.SizesCsv,
        s.GradesCsv,
        s.PalletTypesCsv,
        s.LabelIDsCsv,
        s.StyleIDsCsv,
        s.MethodIDsCsv,
        s.UPCIDsCsv,
        s.PIDBagIDsCsv,
        s.TiHiIDsCsv,
        s.BWCDisplay,
        s.ShippingNotes,
        s.QualityNotes,
        s.PackagingNotes
      ].filter(Boolean).join(' ');
      const hayNorm = normalizeForSearch(hayRaw);
      return tokens.every(t => hayNorm.includes(t));
    });
  }, [allSpecs, requiredSelected, customerId, domesticImport, commodityId, selectedSizes, selectedStyles, search, parseCsvList, resolveCommodityIdForSpec]);

  // Split into universal and non-universal under required filters
  const universalRequired = useMemo(
    () => baseRequiredFiltered.filter(s => Boolean(s.Universal)),
    [baseRequiredFiltered]
  );
  const nonUniversalRequired = useMemo(
    () => baseRequiredFiltered.filter(s => !Boolean(s.Universal)),
    [baseRequiredFiltered]
  );

  // Destinations offered: only those carried by specs that survive every other
  // filter (search, commodity, sizes, styles) — the Entry Form's Customers-table
  // list is authoring-side and would offer locations with no matching spec.
  // Universal cards are excluded: they show at every destination, so they must
  // not conjure destination options of their own.
  const destinationOptions = useMemo(() => {
    if (!requiredSelected) return [];
    const set = new Set();
    (nonUniversalRequired || []).forEach((s) => {
      parseCsvList(s.DestinationsCsv).forEach((d) => {
        const v = String(d || '').trim();
        if (v && v !== ALL_DEST_TOKEN) set.add(v);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [requiredSelected, nonUniversalRequired, parseCsvList]);

  // A selected destination can fall out of range when another filter narrows the
  // list; clear it rather than leave the grid pinned to an unreachable value.
  useEffect(() => {
    if (!destination || destination === DEST_ALL_FILTER) return;
    if (!destinationOptions.includes(destination)) setDestination('');
  }, [destination, destinationOptions]);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);
  const getCardName = useCallback((s) => (String(s.SpecName || '').trim() || ''), []);
  const sortByCardName = useCallback((a, b) => collator.compare(getCardName(a), getCardName(b)), [collator, getCardName]);

  // Final list of specs to display based on selection state:
  // - If only required filters are selected: show Universal cards that match required filters
  // - If destination is selected: show Universal (still only required filters) first, then other cards that meet destination too
  const displayedSpecs = useMemo(() => {
    if (!requiredSelected) return [];
    if (!destination) {
      return [...universalRequired].sort(sortByCardName);
    }
    if (destination === DEST_ALL_FILTER) {
      const universalSorted = [...universalRequired].sort(sortByCardName);
      const nonUniversalSorted = [...nonUniversalRequired].sort(sortByCardName);
      return [...universalSorted, ...nonUniversalSorted];
    }
    const meetsDestination = (s) => {
      const dests = parseCsvList(s.DestinationsCsv);
      return dests.includes(ALL_DEST_TOKEN) || dests.includes(destination);
    };
    const nonUniversalWithDestination = nonUniversalRequired.filter(meetsDestination).sort(sortByCardName);
    const universalSorted = [...universalRequired].sort(sortByCardName);
    return [...universalSorted, ...nonUniversalWithDestination];
  }, [requiredSelected, destination, universalRequired, nonUniversalRequired, sortByCardName, parseCsvList]);

  // If there are NO "All Destinations" cards for required selection, Destination becomes required
  const destinationIsRequired = useMemo(
    () => requiredSelected && universalForRequiredOnly.length === 0,
    [requiredSelected, universalForRequiredOnly.length]
  );
  // Commodity options should reflect the currently matching cards on the right (respect other filters)
  // This intentionally ignores the currently selected commodity so the options represent what's available.
  const baseRequiredFilteredWithoutCommodity = useMemo(() => {
    if (!requiredSelected) return [];
    const q = search.trim();
    const normalizeForSearch = (str) => String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
    const tokens = q.split(/\s+/).map(normalizeForSearch).filter(Boolean);
    return (allSpecs || []).filter(s => {
      if (String(s.CustomerID) !== String(customerId)) return false;
      if (String(s.DomesticOrImport || '') !== domesticImport) return false;
      // NOTE: commodity filter is intentionally excluded here
      if ((selectedSizes || []).length > 0) {
        const sizesCsv = parseCsvList(s.SizesCsv);
        const sizesSet = new Set(sizesCsv);
        const matchesAll = (selectedSizes || []).every(sz => sizesSet.has(sz));
        if (!matchesAll) return false;
      }
      if ((selectedStyles || []).length > 0) {
        const styleIds = String(s.StyleIDsCsv || '').split(',').map(x => Number(String(x).trim())).filter(v => !Number.isNaN(v));
        const styleSet = new Set(styleIds);
        const matchesAllStyles = (selectedStyles || []).every(id => styleSet.has(Number(id)));
        if (!matchesAllStyles) return false;
      }
      if (tokens.length === 0) return true;
      const hayRaw = [
        s.CustomerName,
        s.SpecName,
        s.CommodityName || s.Commodity,
        s.CaseTagName,
        s.DestinationsCsv,
        s.VarietiesCsv,
        s.SizesCsv,
        s.GradesCsv,
        s.PalletTypesCsv,
        s.LabelIDsCsv,
        s.StyleIDsCsv,
        s.MethodIDsCsv,
        s.UPCIDsCsv,
        s.PIDBagIDsCsv,
        s.TiHiIDsCsv,
        s.BWCDisplay,
        s.ShippingNotes,
        s.QualityNotes,
        s.PackagingNotes
      ].filter(Boolean).join(' ');
      const hayNorm = normalizeForSearch(hayRaw);
      return tokens.every(t => hayNorm.includes(t));
    });
  }, [allSpecs, requiredSelected, customerId, domesticImport, selectedSizes, selectedStyles, search, parseCsvList]);

  const universalRequiredWithoutCommodity = useMemo(
    () => baseRequiredFilteredWithoutCommodity.filter(s => Boolean(s.Universal)),
    [baseRequiredFilteredWithoutCommodity]
  );
  const nonUniversalRequiredWithoutCommodity = useMemo(
    () => baseRequiredFilteredWithoutCommodity.filter(s => !Boolean(s.Universal)),
    [baseRequiredFilteredWithoutCommodity]
  );

  const displayedSpecsWithoutCommodity = useMemo(() => {
    if (!requiredSelected) return [];
    if (!destination) {
      return [...universalRequiredWithoutCommodity].sort(sortByCardName);
    }
    if (destination === DEST_ALL_FILTER) {
      const universalSorted = [...universalRequiredWithoutCommodity].sort(sortByCardName);
      const nonUniversalSorted = [...nonUniversalRequiredWithoutCommodity].sort(sortByCardName);
      return [...universalSorted, ...nonUniversalSorted];
    }
    const meetsDestination = (s) => {
      const dests = parseCsvList(s.DestinationsCsv);
      return dests.includes(ALL_DEST_TOKEN) || dests.includes(destination);
    };
    const nonUniversalWithDestination = nonUniversalRequiredWithoutCommodity.filter(meetsDestination).sort(sortByCardName);
    const universalSorted = [...universalRequiredWithoutCommodity].sort(sortByCardName);
    return [...universalSorted, ...nonUniversalWithDestination];
  }, [
    requiredSelected,
    destination,
    universalRequiredWithoutCommodity,
    nonUniversalRequiredWithoutCommodity,
    sortByCardName,
    parseCsvList
  ]);

  const commodityOptions = useMemo(() => {
    if (!requiredSelected) return [];
    const ids = new Set(
      (displayedSpecsWithoutCommodity || [])
        .map(resolveCommodityIdForSpec)
        .filter((v) => Number.isFinite(v) && v > 0)
    );
    return (commodities || []).filter(c => ids.has(Number(c.UniqueID)));
  }, [requiredSelected, displayedSpecsWithoutCommodity, resolveCommodityIdForSpec, commodities]);

  const resolveLogo = useCallback((spec) => {
    // Derive filename using the same slugging logic as server uploads
    const baseName = slugifyCompany(spec.CustomerName || '') || 'Logo';
    return {
      folder: 'CustomerSpecs/CustomerLogos',
      src: `${baseName}.webp`,
      alt: `${String(spec.CustomerName || baseName)} Logo`
    };
  }, []);

  const renderCard = (spec) => {
    const logo = resolveLogo(spec);
    const pill = colorForCommodity(spec.CommodityName || spec.Commodity || '');

    // Secondary line removed per request

    const hasHeader = !!spec.Universal;
    return (
      <CardWrapper key={spec.UniqueID} onClick={() => { setPanelSpec(spec); setPanelOpen(true); }}>
        {hasHeader ? <UniversalHeader>All Destinations</UniversalHeader> : null}
        <ImageWrapper $short={hasHeader}>
          {logo ? (
            <SecureImage
              src={logo.src}
              folder={logo.folder}
              alt={logo.alt}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', fontWeight:800, fontSize:'1.3rem' }}>
              {spec.CustomerName || 'Customer'}
            </div>
          )}
        </ImageWrapper>
        <InfoSection>
          {spec.CommodityName ? <div><Pill $bg={pill.bg} $border={pill.border}>{spec.CommodityName}</Pill></div> : null}
          <div style={{ marginTop: 6, fontWeight: 800 }}>{getCardName(spec)}</div>
        </InfoSection>
      </CardWrapper>
    );
  };

  return (
    <>
    <PageWrapper>
      <FiltersContainer>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <Label>Search</Label>
            <ClearButton
              type="button"
              $active={anyFiltersSelected}
              disabled={!anyFiltersSelected}
              onClick={clearAllFilters}
              aria-disabled={!anyFiltersSelected}
              aria-label="Clear filters"
            >
              Clear
            </ClearButton>
          </div>
          <StyledInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." disabled={!requiredSelected} />
        </div>
        <div>
          <Label>Customer <span style={{ color: 'var(--color-danger)' }}>*</span></Label>
          <TypeaheadSelect
            ariaLabel="Customer"
            value={customerId}
            placeholder="Select customer"
            size="md"
            options={customerOptions}
            onChange={(val) => {
              setCustomerId(String(val || ''));
              setDestination('');
              setCommodityId('');
            }}
          />
        </div>
        <div>
          <Label>Import or Domestic <span style={{ color: 'var(--color-danger)' }}>*</span></Label>
          <TypeaheadSelect
            ariaLabel="Import or Domestic"
            value={domesticImport}
            placeholder="Select..."
            size="sm"
            options={[{ value: 'Domestic', label: 'Domestic' }, { value: 'Import', label: 'Import' }]}
            onChange={(val) => { setDomesticImport(String(val || '')); setDestination(''); setCommodityId(''); }}
          />
        </div>
        <div>
          <Label>
            Destination
            {destinationIsRequired ? <span style={{ color: 'var(--color-danger)' }}> *</span> : null}
          </Label>
          <TypeaheadSelect
            ariaLabel="Destination"
            value={destination}
            placeholder="Select destination"
            size="md"
            disabled={!requiredSelected}
            options={destinationOptions.map((d) => ({ value: d, label: d }))}
            onChange={(val) => {
              const v = String(val || '');
              setDestination(v);
              setCommodityId('');
            }}
          />
        </div>
        <div>
          <Label>Commodity</Label>
          <TypeaheadSelect
            ariaLabel="Commodity"
            value={commodityId}
            placeholder="Select commodity"
            size="md"
            disabled={!requiredSelected}
            options={commodityOptions.map((c) => ({ value: String(c.UniqueID), label: String(c.Commodity || '') }))}
            onChange={(val) => setCommodityId(String(val || ''))}
          />
        </div>
        <div>
          <Label>Size Options</Label>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <TypeaheadSelect
              ariaLabel="Add size"
              value=""
              placeholder="Add size..."
              size="md"
              onChange={(val) => {
                const v = String(val || '').trim();
                if (!v) return;
                setSelectedSizes(prev => Array.from(new Set([...(prev || []), v])));
              }}
              disabled={!requiredSelected || !commodityId}
              options={availableSizeAddOptions.map((s) => ({ value: s, label: s }))}
            />
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {(selectedSizes || []).map(s => (
                <span
                  key={s}
                  onClick={() => setSelectedSizes(prev => (prev || []).filter(x => x !== s))}
                  style={{ background:'var(--green-800)', color:'var(--white)', padding:'4px 10px', borderRadius:12, cursor:'pointer' }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div>
          <Label>Styles</Label>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <TypeaheadSelect
              ariaLabel="Add style"
              value=""
              placeholder="Add style..."
              size="md"
              onChange={(val) => {
                const v = String(val || '').trim();
                if (!v) return;
                setSelectedStyles(prev => Array.from(new Set([...(prev || []), Number(v)])));
              }}
              disabled={!requiredSelected}
              options={availableStyleAddOptions.map((st) => ({ value: String(st.UniqueID), label: String(st.Style || '') }))}
            />
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {(selectedStyles || []).map(id => (
                <span
                  key={id}
                  onClick={() => setSelectedStyles(prev => (prev || []).filter(x => Number(x) !== Number(id)))}
                  style={{ background:'var(--green-800)', color:'var(--white)', padding:'4px 10px', borderRadius:12, cursor:'pointer' }}
                >
                  {styleNameById[Number(id)] || id}
                </span>
              ))}
            </div>
          </div>
        </div>
      </FiltersContainer>
      <ContentWrapper>
        <TopBar>
          <div style={{ fontWeight: 700 }}>Customer Spec Index</div>
        </TopBar>
        <ScrollableContent>
          {loading ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              {Array.from({ length: 6 }).map((_, i) => (<SkeletonCard key={i} />))}
            </div>
          ) : (!requiredSelected) ? (
            <NoResultsMessage>Select a customer and import/domestic to see specs</NoResultsMessage>
          ) : (displayedSpecs.length === 0 ? (
            specsForRequiredOnly.length === 0 ? (
              <NoResultsMessage>No specs found for this customer and import/domestic</NoResultsMessage>
            ) : (destinationIsRequired && !destination) ? (
              <NoResultsMessage>No all destination cards, select destination</NoResultsMessage>
            ) : (
              <NoResultsMessage>No results found</NoResultsMessage>
            )
          ) : (
            <CardGrid>
              {displayedSpecs.map(renderCard)}
            </CardGrid>
          ))}
        </ScrollableContent>
      </ContentWrapper>

      {panelOpen && panelSpec && (
      <IndexPanelBackdrop onClick={() => { setPanelOpen(false); setPanelSpec(null); }} role="dialog" aria-modal="true">
        <IndexPanelContainer onClick={(e) => e.stopPropagation()}>
          <PanelCloseButton aria-label="Close" onClick={() => { setPanelOpen(false); setPanelSpec(null); }}>×</PanelCloseButton>
          <IndexPanelContent>
            <ColumnGrid>
              <ColumnCard>
                <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
                <FirstSection>
                  <LogoBox>
                    {(() => {
                      const logo = resolveLogo(panelSpec);
                      return (
                        <SecureImage
                          src={logo.src}
                          folder={logo.folder}
                          alt={logo.alt}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      );
                    })()}
                  </LogoBox>
                  <Row>
                    {(() => {
                      const pill = colorForCommodity(panelSpec.CommodityName || panelSpec.Commodity || '');
                      const text = panelSpec.CommodityName || panelSpec.Commodity || '';
                      return text ? <Pill $bg={pill.bg} $border={pill.border}>{text}</Pill> : null;
                    })()}
                    <GreyBadge>{panelSpec.DomesticOrImport || domesticImport || ''}</GreyBadge>
                  </Row>
                  {/* Destinations (under commodity, expandable) */}
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    {(() => {
                      const arr = parseCsvList(panelSpec.DestinationsCsv || '');
                      if (!arr.length) return null;
                      const hasAll = arr.includes(ALL_DEST_TOKEN);
                      // If this is a Universal card, we already show the orange "All Destinations"
                      // strip above the title; avoid duplicating it here.
                      if (hasAll) return panelSpec.Universal ? null : <GreyBadge>All Destinations</GreyBadge>;
                      const maxShown = 3;
                      const visible = panelDestExpanded ? arr : arr.slice(0, maxShown);
                      const remaining = Math.max(0, arr.length - maxShown);
                      return (
                        <>
                          {visible.map((d, i) => <GreyBadge key={`dest-${i}`}>{d}</GreyBadge>)}
                          {remaining > 0 && !panelDestExpanded ? (
                            <button
                              type="button"
                              aria-label="Show more destinations"
                              onClick={() => setPanelDestExpanded(true)}
                              style={{
                                width: 26, height: 26, borderRadius: 13,
                                background:'var(--green-800)', color:'var(--white)', border:'none',
                                display:'inline-flex', alignItems:'center', justifyContent:'center',
                                fontWeight: 800, cursor:'pointer'
                              }}
                            >
                              +
                            </button>
                          ) : null}
                          {panelDestExpanded && arr.length > maxShown ? (
                            <button
                              type="button"
                              aria-label="Show fewer destinations"
                              onClick={() => setPanelDestExpanded(false)}
                              style={{
                                width: 26, height: 26, borderRadius: 13,
                                background:'var(--grey-700)', color:'var(--white)', border:'none',
                                display:'inline-flex', alignItems:'center', justifyContent:'center',
                                fontWeight: 800, cursor:'pointer'
                              }}
                            >
                              −
                            </button>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                  <SpecNameGroup>
                    {panelSpec.Universal ? <UniversalStrip>All Destinations</UniversalStrip> : null}
                    <GreenTitle $withHeader={!!panelSpec.Universal}>{String(panelSpec.SpecName || '').trim() || 'Specification'}</GreenTitle>
                  </SpecNameGroup>
                </FirstSection>
                <div style={{ marginTop:'auto', paddingBottom:5, textAlign:'center' }}>
                  {(() => {
                    const by = panelSpec.UpdatedBy || panelSpec.CreatedBy || '—';
                    const ts = panelSpec.UpdatedAt || panelSpec.CreatedAt || null;
                    const dateStr = ts ? new Date(ts).toLocaleDateString('en-US') : '—';
                    return (
                      <>
                        <FooterDate>Last Edit Date: {dateStr}</FooterDate>
                        <FooterEditor>{by}</FooterEditor>
                      </>
                    );
                  })()}
                </div>
                </div>
              </ColumnCard>
              <ColumnCard aria-label="Column 2">
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* Technical Specifications */}
                  <GroupCard>
                    <GroupHeaderBar>Technical Specifications</GroupHeaderBar>
                    <TableGrid>
                      {(() => {
                        const list = [];
                        const gradeNames = parseCsvList(panelSpec.GradesCsv);
                        const gradesEl = gradeNames.length ? (
                          <ValuePillWrap>
                            {gradeNames.map((g, gi) => (
                              <ValuePill key={`${g}-${gi}`} style={{ background: gradeColor(g).bg }}>{g}</ValuePill>
                            ))}
                          </ValuePillWrap>
                        ) : '';
                        const varieties = parseCsvList(panelSpec.VarietiesCsv).join('\n');
                        const bwcType = String(panelSpec.BWCType || '').trim();
                        const bwcDisplay = String(panelSpec.BWCDisplay || (bwcType === 'Bulk' ? 'BULK' : panelSpec.BWCValue || '')).trim();
                        const pluImgs = panelPluImages || [];
                        const custItem = String(panelSpec.CustomerItemNumber || '').trim();
                        list.push(['Grades', gradesEl]);
                        list.push(['Varieties', varieties]);
                        if (bwcType) list.push([bwcType === 'Weight' ? 'Weight' : (bwcType === 'Count' ? 'Count' : 'Bulk'), bwcDisplay]);
                        list.push(['PLU', (pluImgs.length ? (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
                            {pluImgs.map((src, i) => (
                              <img
                                key={i}
                                src={src}
                                alt="PLU"
                                onClick={() => setLightboxSrc(src)}
                                style={{ height: 48, borderRadius: 6, border: '1px solid var(--shadow-a08)', cursor: 'zoom-in' }}
                              />
                            ))}
                          </div>
                        ) : '')]);
                        list.push(['Customer Item Number', custItem]);
                        return list.map(([k, v], i) => (
                          <TableRow key={`${k}-${i}`}>
                            <CellKey>{k}</CellKey>
                            <CellValue>{v || ''}</CellValue>
                          </TableRow>
                        ));
                      })()}
                    </TableGrid>
                  </GroupCard>

                  {/* Sizing */}
                  <GroupCard>
                    <GroupHeaderBar>Sizing</GroupHeaderBar>
                    <TableGrid>
                      {(() => {
                        const recommendedSizing = parseCsvList(panelSpec.RecommendedSizingCsv || panelSpec.SizesCsv);
                        const specSizeRange = parseCsvList(panelSpec.SpecSizeRangeCsv);
                        const subbing = parseCsvList(panelSpec.SubbingOptionsCsv);
                        const approval = String(panelSpec.ApprovalNeeded || '').trim();
                        const renderPills = (arr) => {
                          const list = Array.isArray(arr) ? arr : [];
                          if (!list.length) return '';
                          return (
                            <ValuePillWrap $left>
                              {list.map((v, i) => <ValuePill key={`${v}-${i}`}>{v}</ValuePill>)}
                            </ValuePillWrap>
                          );
                        };
                        return (
                          <>
                            <TableRow>
                              <CellKey>Recommended Sizing</CellKey>
                              <CellValue>{renderPills(recommendedSizing)}</CellValue>
                            </TableRow>
                            <TableRow>
                              <CellKey>Spec Size Range</CellKey>
                              <CellValue>{renderPills(specSizeRange)}</CellValue>
                            </TableRow>
                            <TableRow>
                              <CellKey>Subbing Options</CellKey>
                              <CellValue>{renderPills(subbing)}</CellValue>
                            </TableRow>
                            <TableRow>
                              <CellKey>Approval Needed</CellKey>
                              <CellValue>{approval || ''}</CellValue>
                            </TableRow>
                            <DividerRow>Sizing Notes</DividerRow>
                            <NotesArea>{String(panelSpec.SizingNotes || '').trim()}</NotesArea>
                          </>
                        );
                      })()}
                    </TableGrid>
                  </GroupCard>

                  {/* Quality Notes */}
                  <GroupCard>
                    <GroupHeaderBar>Quality Notes</GroupHeaderBar>
                    <TableGrid>
                      <NotesArea>{String(panelSpec.QualityNotes || '').trim()}</NotesArea>
                    </TableGrid>
                  </GroupCard>

                  {/* Storage group removed; Temperature Recorder moved to Shipping Specifications */}
                </div>
              </ColumnCard>
              <ColumnCard aria-label="Column 3">
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* Package Specification */}
                  <GroupCard>
                    <GroupHeaderBar>Package Specification</GroupHeaderBar>
                    <TableGrid>
                      {(() => {
                        const styleIds = parseCsvList(panelSpec.StyleIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                        const labelIdArr = parseCsvList(panelSpec.LabelIDsCsv);
                        const methodIds = parseCsvList(panelSpec.MethodIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                        const methods = methodIds.map(id => ({ id, name: (methodsById ? (methodsById[id] || String(id)) : String(id)) }));
                        // Per-entry grade assignments (read-only, M6) — resolved {value,grades} from GET /.
                        const styleGradesById = new Map((panelSpec.StyleAssignments || []).map(a => [Number(a.value), a.grades || []]));
                        const methodGradesById = new Map((panelSpec.MethodAssignments || []).map(a => [Number(a.value), a.grades || []]));
                        const labelGradesByValue = new Map((panelSpec.LabelAssignments || []).map(a => [String(a.value), a.grades || []]));
                        // Deduped spec-grade count so a covers-all entry collapses to one grey ALL chip.
                        const specGradeCount = new Set(parseCsvList(panelSpec.GradesCsv)).size;
                        const gtin = String(panelSpec.GTIN || '').trim();
                        // UPC barcodes: prefer normalized typed refs, then fall back to legacy ID CSVs.
                        const upcIds = parseCsvList(panelSpec.UPCIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                        const bagUpcIds = parseCsvList(panelSpec.PIDBagIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                        const upcRefValues = Array.isArray(panelSpec.UPCRefs)
                          ? panelSpec.UPCRefs.map(ref => String(ref?.value || '').trim()).filter(Boolean)
                          : parseCsvList(panelSpec.UPCRefsCsv);
                        // Manual UPC/EAN bypass (manual UPC plan, M3): raw typed digits, no catalog
                        // lookup needed — barcodeConfigForUpc classifies by length (12 -> UPC-A,
                        // 13 -> EAN-13). A custom-mode spec carries no catalog UPC refs.
                        const manualUpcs = Array.isArray(panelSpec.ManualUPCs)
                          ? panelSpec.ManualUPCs
                          : parseCsvList(panelSpec.ManualUPCsCsv);
                        const barcodeConfigs = (() => {
                          const seen = new Set();
                          const configs = [];
                          const add = (raw) => {
                            const config = barcodeConfigForUpc(raw);
                            if (!config || seen.has(config.value)) return;
                            seen.add(config.value);
                            configs.push(config);
                          };
                          manualUpcs.forEach((d) => add(d));
                          if (upcRefByValue) upcRefValues.forEach(value => add(upcRefByValue[value]?.digits));
                          if (upcMap) upcIds.forEach(id => add(upcMap[id]));
                          if (pidBagUpcById) bagUpcIds.forEach(id => add(pidBagUpcById[id]));
                          return configs;
                        })();
                        const styleCards = styleIds.map(id => (
                          <AssignedItemCard
                            key={id}
                            name={(stylesById ? stylesById[id] : id) || id}
                            grades={styleGradesById.get(id)}
                            specGradeCount={specGradeCount}
                          />
                        ));
                        const labelCards = (labelIdArr || []).map(lbl => (
                              <AssignedItemCard
                                key={lbl}
                                name={lbl === NO_PLU_LABEL ? NO_PLU_DISPLAY : lbl}
                                grades={labelGradesByValue.get(String(lbl))}
                                specGradeCount={specGradeCount}
                                title="View label image"
                                onClick={async () => {
                                  try {
                                    const bagRows = (bagsByLabelId && bagsByLabelId[String(lbl)]) || [];
                                    const allImgs = [];
                                    const nextBagCache = { ...bagImagesCache };
                                    const nextPluCache = { ...pluImagesCache };
                                    for (const row of bagRows) {
                                      const id = Number(row.UniqueID);
                                      if (!Number.isFinite(id)) continue;
                                      let imgs = nextBagCache[id];
                                      if (!imgs) {
                                        try {
                                          const r = await authAxios.get(`/api/pidbags/images/${id}`);
                                          const arr = Array.isArray(r.data) ? r.data : [];
                                          imgs = arr.map(buildPidBagsImageUrl).filter(Boolean);
                                        } catch { imgs = []; }
                                        nextBagCache[id] = imgs;
                                      }
                                      imgs.forEach(u => allImgs.push(u));
                                    }
                                    // Also include PLU images when LabelID belongs to PIDPLU
                                    const pluRows = (pluByLabelId && pluByLabelId[String(lbl)]) || [];
                                    for (const pr of pluRows) {
                                      const pid = Number(pr.UniqueID);
                                      if (!Number.isFinite(pid)) continue;
                                      let pimgs = nextPluCache[pid];
                                      if (!pimgs) {
                                        try {
                                          const r = await authAxios.get(`/api/pidplu/images?pluId=${pid}`);
                                          pimgs = Array.isArray(r.data) ? r.data : [];
                                        } catch { pimgs = []; }
                                        nextPluCache[pid] = pimgs;
                                      }
                                      pimgs.forEach(u => allImgs.push(u));
                                    }
                                    setBagImagesCache(nextBagCache);
                                    setPluImagesCache(nextPluCache);
                                    const uniq = Array.from(new Set(allImgs));
                                    if (uniq.length) setLightboxGallery(uniq);
                                  } catch (e) {
                                    console.error('Failed to load LabelID images', e);
                                  }
                                }}
                                />
                            ));
                        const methodCards = methods.map(m => (
                              <AssignedItemCard
                                key={m.id}
                                name={m.name}
                                grades={methodGradesById.get(m.id)}
                                specGradeCount={specGradeCount}
                                title="View method image"
                                onClick={async () => {
                                  try {
                                    let imgs = methodImagesCache[m.id];
                                    if (!imgs) {
                                      const res = await authAxios.get('/api/pidmethods/images', { params: { methodId: m.id } });
                                      imgs = Array.isArray(res.data) ? res.data : [];
                                      imgs = imgs.map((p) => {
                                        const val = String(p || '');
                                        if (val.startsWith('http')) return val;
                                        const filename = val.replace(/^\/+/,'').replace(/^PIDMethods\//i, '');
                                        return `${CDN_BASE}/PIDMethods/${encodeURIComponent(filename)}`;
                                      });
                                      setMethodImagesCache(prev => ({ ...prev, [m.id]: imgs }));
                                    }
                                    if (imgs && imgs.length) setLightboxGallery(imgs);
                                  } catch (e) {
                                    console.error('Failed to load method images', e);
                                  }
                                }}
                                />
                            ));

                        return (
                          <>
                            <AssignedTier>
                              <AssignedTierLabel>Styles</AssignedTierLabel>
                              <AssignedItemsWrap>{styleCards}</AssignedItemsWrap>
                            </AssignedTier>
                            <AssignedTier>
                              <AssignedTierLabel>Label ID</AssignedTierLabel>
                              <AssignedItemsWrap>{labelCards}</AssignedItemsWrap>
                            </AssignedTier>
                            <AssignedTier>
                              <AssignedTierLabel>Methods</AssignedTierLabel>
                              <AssignedItemsWrap>{methodCards}</AssignedItemsWrap>
                            </AssignedTier>
                            <TableRow>
                              <CellKey>GTIN</CellKey>
                              <CellValue>{gtin || ''}</CellValue>
                            </TableRow>
                            <TableRow>
                              <CellKey>UPC</CellKey>
                              <CellValue>
                                {barcodeConfigs.length ? (
                                  <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
                                    {barcodeConfigs.map((config) => (
                                      <Barcode
                                        key={`${config.format}-${config.value}`}
                                        value={config.value}
                                        format={config.format}
                                        height={40}
                                        width={1.6}
                                        displayValue
                                      />
                                    ))}
                                  </div>
                                ) : ''}
                              </CellValue>
                            </TableRow>
                          </>
                        );
                      })()}
                      <DividerRow>Packaging Notes</DividerRow>
                      <NotesArea>{String(panelSpec.PackagingNotes || '').trim()}</NotesArea>
                    </TableGrid>
                  </GroupCard>

                  {/* Shipping Specifications (Ti x Hi, Pallet Type, Temperature Recorder, Shipping Notes) */}
                  <GroupCard>
                    <GroupHeaderBar>Shipping Specifications</GroupHeaderBar>
                    <TableGrid>
                      <TableRow>
                        <CellKey>Ti x Hi</CellKey>
                        <CellValue>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
                            {(() => {
                              const tihiIds = parseCsvList(panelSpec.TiHiIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                              return tihiIds.map(id => {
                                const rec = tihiById?.[id];
                                const label = rec?.TiHi || id;
                                const link = rec?.ImageLink || '';
                                return (
                                  <span
                                    key={`tihi-pill-${id}`}
                                    onClick={() => {
                                      if (!link) return;
                                      const url = (() => {
                                        const val = String(link);
                                        if (val.startsWith('http')) return val;
                                        const filename = val.replace(/^\/+/,'').replace(/^CustomerSpecs\/TiHi\//i, '');
                                        return `${CDN_BASE}/CustomerSpecs/TiHi/${encodeURIComponent(filename)}`;
                                      })();
                                      setLightboxGallery([url]);
                                    }}
                                    style={{ background:'var(--green-800)', color:'var(--white)', padding:'4px 10px', borderRadius:12, cursor:'zoom-in' }}
                                  >
                                    {label}
                                  </span>
                                );
                              });
                            })()}
                          </div>
                        </CellValue>
                      </TableRow>
                      <TableRow>
                        <CellKey>Pallet Type</CellKey>
                        <CellValue>{parseCsvList(panelSpec.PalletTypesCsv).join(', ')}</CellValue>
                      </TableRow>
                      <TableRow>
                        <CellKey>Temperature Recorder</CellKey>
                        <CellValue>{String(panelSpec.TemperatureRecorder || '').trim()}</CellValue>
                      </TableRow>
                      <DividerRow>Shipping Notes</DividerRow>
                      <NotesArea>{String(panelSpec.ShippingNotes || '').trim()}</NotesArea>
                    </TableGrid>
                  </GroupCard>
                </div>
              </ColumnCard>
              <ColumnCard aria-label="Column 4">
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* Case Tag */}
                  <GroupCard>
                    <GroupHeaderBar>Case Tag</GroupHeaderBar>
                    <TableGrid>
                      {(() => {
                        const name = String(panelSpec.CaseTagName || '').trim();
                        const row = name && caseTagsByName ? caseTagsByName[name] : null;
                        // ImageLink is stored as a bare filename (e.g. "WALMART-DOMESTIC-BAG.webp");
                        // the blob lives under CustomerSpecs/CaseTag. Build the CDN URL the same way
                        // the Ti×Hi pills above do, tolerating a full URL or foldered legacy value.
                        const img = (() => {
                          const val = String(row?.ImageLink || '').trim();
                          if (!val) return '';
                          if (val.startsWith('http')) return val;
                          const filename = val.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
                          return filename ? `${CDN_BASE}/CustomerSpecs/CaseTag/${encodeURIComponent(filename)}` : '';
                        })();
                        return (
                          <NotesArea>
                            {img ? (
                              <div style={{ display:'flex', justifyContent:'center' }}>
                                <img
                                  src={img}
                                  alt={name || 'Case Tag'}
                                  onClick={() => setLightboxSrc(img)}
                                  style={{ maxWidth:'100%', maxHeight:'min(220px, 40vh)', width:'auto', height:'auto', objectFit:'contain', borderRadius:8, border:'1px solid var(--shadow-a08)', cursor:'zoom-in' }}
                                />
                              </div>
                            ) : (name || '')}
                          </NotesArea>
                        );
                      })()}
                    </TableGrid>
                  </GroupCard>

                  {/* Bag Label */}
                  <GroupCard>
                    <GroupHeaderBar>Bag Label</GroupHeaderBar>
                    <TableGrid>
                      <NotesArea>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>Bag Label Notes</div>
                        {String(panelSpec.BagLabelNotes || '').trim()}
                      </NotesArea>
                      {(() => {
                        if (!bagLabelRows) return null;
                        const custId = canonicalCustomerId(customers.dropdown, panelSpec);
                        // Commodity is a rule axis (Customers > Commodity > Type > Style);
                        // reuse the same id resolution the filters use (id else name).
                        const commId = resolveCommodityIdForSpec(panelSpec);
                        const styleIds = parseCsvList(panelSpec.StyleIDsCsv).map(n => Number(n)).filter(v => !Number.isNaN(v));
                        const selByStyle = new Map((panelSpec.StyleAssignments || []).map(a => [Number(a.value), a.bagLabels ?? null]));
                        const styleRowById = new Map((stylesAll || []).map(s => [Number(s.UniqueID), s]));
                        // Group per resolved label, styles in spec order / labels in first-appearance
                        // order; styles resolving to zero labels pool into one "No Bag Label" row.
                        const groups = new Map();
                        const noLabel = [];
                        styleIds.forEach((id) => {
                          const styleRow = styleRowById.get(id) || { UniqueID: id, Type: null };
                          const resolved = resolveForStyle(selByStyle.get(id) ?? null, bagLabelRows, custId, styleRow, commId);
                          if (!resolved.length) { noLabel.push(id); return; }
                          resolved.forEach((l) => {
                            const k = Number(l.UniqueID);
                            if (!groups.has(k)) groups.set(k, { label: l, styleIds: [] });
                            groups.get(k).styleIds.push(id);
                          });
                        });
                        if (!groups.size && !noLabel.length) return null;
                        const stylePills = (ids) => (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                            {ids.map((id) => (
                              <span key={id} style={{ background:'var(--green-800)', color:'var(--white)', padding:'4px 10px', borderRadius:12, whiteSpace:'nowrap' }}>
                                {(stylesById && stylesById[id]) || id}
                              </span>
                            ))}
                          </div>
                        );
                        return (
                          <div style={{ gridColumn:'1 / -1', padding:'10px 12px', display:'grid', gridTemplateColumns:'auto 1fr', columnGap:12, rowGap:10, alignItems:'center' }}>
                            {Array.from(groups.values()).map(({ label, styleIds: sids }) => {
                              const filename = toFilename(label.ImageLink);
                              return (
                                <React.Fragment key={`bl-${label.UniqueID}`}>
                                  {filename ? (
                                    <SecureImage
                                      src={filename}
                                      folder={BAGLABEL_FOLDER}
                                      alt={label.LabelName || 'Bag Label'}
                                      onClick={() => setLightboxSrc(`${CDN_BASE}/${BAGLABEL_FOLDER}/${encodeURIComponent(filename)}`)}
                                      style={{ height: 64, maxWidth: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--shadow-a08)', cursor: 'zoom-in' }}
                                    />
                                  ) : (
                                    <span style={{ fontWeight: 700 }}>{label.LabelName}</span>
                                  )}
                                  {stylePills(sids)}
                                </React.Fragment>
                              );
                            })}
                            {noLabel.length ? (
                              <>
                                <span style={{ fontStyle:'italic', color:'var(--shadow-a55)' }}>No Bag Label</span>
                                {stylePills(noLabel)}
                              </>
                            ) : null}
                          </div>
                        );
                      })()}
                    </TableGrid>
                  </GroupCard>
                </div>
              </ColumnCard>
            </ColumnGrid>
          </IndexPanelContent>
          {(lightboxSrc || (lightboxGallery && lightboxGallery.length)) && (
            <LightboxBackdrop onClick={() => { setLightboxSrc(null); setLightboxGallery(null); }} style={{ zIndex: 3000, position:'fixed' }}>
              <button
                onClick={(e)=>{ e.stopPropagation(); setLightboxSrc(null); setLightboxGallery(null); }}
                aria-label="Close preview"
                style={{ position:'fixed', top:14, right:18, width:36, height:36, borderRadius:18, background:'var(--white-a90)', border:'1px solid var(--shadow-a15)', cursor:'pointer', fontSize:20, lineHeight:1 }}
              >
                ×
              </button>
              {lightboxGallery && lightboxGallery.length ? (
                <div style={{ maxWidth:'95vw', maxHeight:'80vh', overflow:'auto', display:'flex', gap:10, flexWrap:'wrap', justifyContent:'center', alignItems:'center' }} onClick={(e)=>e.stopPropagation()}>
                  {lightboxGallery.map((src, i) => (
                    <LightboxImage key={`gal-${i}`} src={src} alt="Preview" />
                  ))}
                </div>
              ) : (
                <div onClick={(e)=>e.stopPropagation()}>
                  <LightboxImage src={lightboxSrc} alt="Preview" />
                </div>
              )}
            </LightboxBackdrop>
          )}
        </IndexPanelContainer>
      </IndexPanelBackdrop>
      )}
    </PageWrapper>
    </>
  );
};

export default CustomerSpecsIndexPage;


